import React from 'react';
import {
    List, Datagrid, TextField, FunctionField, ShowButton, EditButton, DeleteButton,
    Edit, Show, SimpleShowLayout, SimpleForm, TextInput, SearchInput, Filter, ReferenceField,
    required, Toolbar, SaveButton, TopToolbar,
} from 'react-admin';
import Chip from '@mui/material/Chip';
import CreateUserDialog from '../components/createUserDialog';
import UserRoleEditor from '../components/userRoleEditor';
import { roleLabel } from '../helpers/roles';

// Reachable only by super-admin (and, while the handover is open, admin).
// requireUserManagement in the resolver is what refuses everyone else; the menu
// entry is hidden on the same condition as a convenience, not as the protection.
//
// Bulk actions are off. deleteManyUser is a real mutation that deletes every
// matched account at once, and there is no confirmation wide enough for that to
// be a reasonable thing to offer from a list of people. Deleting is one account
// at a time, on purpose.

const UserFilter = props => (
    <Filter {...props} alwaysOn>
        <SearchInput source="email" alwaysOn />
    </Filter>
);

const ListActions = () => (
    <TopToolbar>
        <CreateUserDialog />
    </TopToolbar>
);

const RoleChips = ({ record }) => (
    <>
        {(record.roles || []).map(role => (
            <Chip key={role} label={roleLabel(role)} size="small" sx={{ marginRight: 0.5 }} />
        ))}
    </>
);

// `actions` must be an ELEMENT, not a component reference. react-admin's
// ListToolbar does `cloneElement(actions, { ...actions.props })`, and
// cloneElement on a bare function reads a `type` that is not there, so the
// toolbar renders an undefined element and the whole page falls over with
// "Element type is invalid ... got: undefined". `filters` and `toolbar` are
// elements here for the same reason; passing any of the three as a component
// breaks the page rather than merely losing the toolbar.
const UserList = props => (
    <List
        {...props}
        perPage={50}
        filters={<UserFilter />}
        actions={<ListActions />}
        sort={{ field: 'email', order: 'ASC' }}
    >
        <Datagrid rowClick="show" bulkActionButtons={false}>
            <TextField source="email" />
            <FunctionField label="Roles" render={record => <RoleChips record={record} />} />
            <ShowButton />
            <EditButton />
            {/* pessimistic, so deleting an account goes through a confirmation
                dialog rather than the undoable one-click default. An account is
                not something to remove with a stray click, and confirmTitle is
                only read by the confirm variant - on the undoable one it is not
                a known prop and leaks into the DOM button. */}
            <DeleteButton
                mutationMode="pessimistic"
                confirmTitle="Delete this account"
                confirmContent="This cannot be undone. The person will lose access immediately."
            />
        </Datagrid>
    </List>
);

// Editing is email only. Roles are assigned through UserRoleEditor on the show
// page, because they go through setUserRoles rather than the composed update -
// see the note in helpers/fields.js.
const EmailToolbar = props => (
    <Toolbar {...props}>
        <SaveButton />
    </Toolbar>
);

const UserEdit = props => (
    <Edit {...props}>
        <SimpleForm toolbar={<EmailToolbar />} redirect="show">
            <TextInput source="email" validate={[required()]} fullWidth />
        </SimpleForm>
    </Edit>
);

// SimpleShowLayout rather than SimpleForm. A <TextField> in react-admin v5
// renders no label of its own - the label comes from the layout wrapping it,
// and SimpleForm only wraps inputs, not read-only fields. On this page that
// left the email and the audit emails sitting there with nothing saying what
// they were. SimpleShowLayout wraps every child in <Labeled>, which is what a
// read-only page wants.
//
// The role editor sits outside it: it is not a field with a source, and it
// needs the record, which <Show> provides to all its children.
const UserShow = props => (
    <Show {...props} queryOptions={{ refetchOnMount: true }}>
        <SimpleShowLayout>
            <TextField source="email" />
            <FunctionField label="Roles" render={record => <RoleChips record={record} />} />
            {/* Who created the account and who last touched it. Resolved to an
                email through the guarded User operations, which is only possible
                because this page is itself behind the user-management boundary.
                Both are absent on accounts that predate the fields, and
                ReferenceField renders nothing for a missing id rather than
                breaking. */}
            <ReferenceField label="Created by" source="createdBy" reference="User" link={false}>
                <TextField source="email" />
            </ReferenceField>
            <ReferenceField label="Last changed by" source="modifiedBy" reference="User" link={false}>
                <TextField source="email" />
            </ReferenceField>
        </SimpleShowLayout>
        <UserRoleEditor />
    </Show>
);

const User = { list: UserList, edit: UserEdit, show: UserShow };
export default User;